import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Target,
  Users,
  Handshake,
  Building2,
  Contact2,
  Calendar,
  Ticket,
  Mail,
  History,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Archive,
  Activity,
  TrendingUp,
  Rocket,
  GitCompare,
  BarChart3,
  Map as MapIcon,
  DollarSign
} from 'lucide-react';
import { useAuth } from '../context/AuthContext.jsx';

const STORAGE_KEY        = 'xsite_sidebar_collapsed';
const GROUP_STORAGE_KEY  = 'xsite_sidebar_groups_open';   // Which groups are currently expanded (JSON: { [groupKey]: true })

const ICON_SIZE = 24;
const ICON_STROKE = 2.25;

// Nav items can be either a leaf `{ to, label, icon, color, end?, adminOnly? }`
// or a group `{ groupKey, label, icon, color, children: [leaf, ...] }`. Groups
// render as an expandable header; clicking toggles the children below.
// Dave 2026-07-09 asked to consolidate the analytics-flavored pages
// under a single "Insights" heading so the sidebar isn't 15+ items tall.
const PRIMARY_NAV = [
  { to: '/',                   label: 'Dashboard',          icon: LayoutDashboard, color: '#0e7490', end: true },
  { to: '/schedule',           label: 'Schedule',           icon: Calendar,        color: '#0891b2' },
  { to: '/tickets',            label: 'Tickets',            icon: Ticket,          color: '#d97706' },
  { to: '/customers',          label: 'Customers',          icon: Users,           color: '#2563eb' },
  {
    groupKey: 'insights',
    label:    'Insights',
    icon:     BarChart3,
    color:    '#0891b2',
    children: [
      { to: '/customer-history',     label: 'Customer History',     icon: History,    color: '#a16207' },
      { to: '/chi',                  label: 'CHI Dashboard',        icon: Users,      color: '#059669' },
      { to: '/retention',            label: 'Retention',            icon: Users,      color: '#7c3aed' },
      { to: '/map',                  label: 'Site Map',             icon: MapIcon,    color: '#0891b2' },
      { to: '/billing-subscription', label: 'Billing & Subscription', icon: DollarSign, color: '#b45309' },
    ]
  },
  { to: '/inactive-sites',     label: 'Inactive Sites',     icon: Archive,         color: '#6b7280' },
  { to: '/onboarding',         label: 'Onboarding Queue',   icon: Rocket,          color: '#0e7490' },
  { to: '/leads',              label: 'Sales Leads',        icon: Target,          color: '#ea580c' },
  { to: '/email-campaigns',    label: 'Email Tracking',     icon: Mail,            color: '#0ea5e9' },
  { to: '/distributors',       label: 'Distributors',       icon: Handshake,       color: '#16a34a' },
  { to: '/strategic-accounts', label: 'Strategic Accounts', icon: Building2,       color: '#7c3aed' },
  { to: '/vendor-contacts',    label: 'Vendor Contacts',    icon: Contact2,        color: '#db2777' },
  // Activity Log is admin-only -- staff and salespeople don't see it
  // at all (handled by the adminOnly filter in the visibleNav useMemo).
  { to: '/activity-log',       label: 'Activity Log',       icon: Activity,        color: '#475569', adminOnly: true },
  // Finance dashboard also admin-only (ARR, top customers, churn).
  { to: '/finance',            label: 'Finance',            icon: TrendingUp,      color: '#047857', adminOnly: true },
  // Reports — admin/staff aggregate views (per-user send counts etc).
  { to: '/reports',            label: 'Reports',            icon: BarChart3,       color: '#9333ea' },
  // Billing Report Diff — compare xsiteio 2026 billing report against
  // local sites. Audit tool for spotting customers we're being billed
  // for that Command doesn't know about.
  { to: '/billing-diff',       label: 'Billing Diff',       icon: GitCompare,      color: '#0891b2' }
];

function initialsFor(user) {
  if (!user) return '?';
  const source = user.name || user.email || '';
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();

  // Salespeople see only Dashboard + Sales Leads. Everyone else sees
  // the full nav minus admin-only entries (Activity Log etc.). For
  // groups, we recursively filter their children and drop the whole
  // group when nothing survives.
  const visibleNav = useMemo(() => {
    function keep(item) {
      if (item.groupKey) return true;   // groups gate by children
      if (item.adminOnly && user?.role !== 'admin') return false;
      if (user?.role === 'salesperson' && item.to !== '/' && item.to !== '/leads') return false;
      return true;
    }
    return PRIMARY_NAV
      .map((item) => {
        if (item.groupKey) {
          const kids = item.children.filter(keep);
          return kids.length > 0 ? { ...item, children: kids } : null;
        }
        return keep(item) ? item : null;
      })
      .filter(Boolean);
  }, [user?.role]);

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === '1';
  });

  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    }
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.sidebar = collapsed ? 'collapsed' : 'expanded';
    }
  }, [collapsed]);

  // Which groups are expanded. Persisted to localStorage. On first
  // load, auto-expand any group whose children contain the current
  // route so the user sees where they are.
  const [groupsOpen, setGroupsOpen] = useState(() => {
    if (typeof localStorage === 'undefined') return {};
    try { return JSON.parse(localStorage.getItem(GROUP_STORAGE_KEY) || '{}'); }
    catch { return {}; }
  });
  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(GROUP_STORAGE_KEY, JSON.stringify(groupsOpen));
    }
  }, [groupsOpen]);
  // Auto-expand when the current URL matches a group's child. Doesn't
  // auto-collapse -- respects the user's manual toggle otherwise.
  useEffect(() => {
    for (const item of PRIMARY_NAV) {
      if (!item.groupKey || !item.children) continue;
      const match = item.children.some((c) => location.pathname === c.to || location.pathname.startsWith(c.to + '/'));
      if (match && !groupsOpen[item.groupKey]) {
        setGroupsOpen((prev) => ({ ...prev, [item.groupKey]: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  function toggleGroup(key) {
    setGroupsOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  const ToggleIcon = collapsed ? ChevronRight : ChevronLeft;

  return (
    <aside className={`app-sidebar ${collapsed ? 'is-collapsed' : ''}`}>
      <div className="sidebar-brand">
        <DateBadge />
        <div className="brand-name">
          <span className="primary">XSite Group</span>
          <span className="secondary">Command · CRM</span>
        </div>
      </div>

      <button
        type="button"
        className="sidebar-toggle"
        onClick={() => setCollapsed((v) => !v)}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <ToggleIcon size={14} strokeWidth={2} />
      </button>

      <nav className="sidebar-nav" aria-label="Primary">
        {visibleNav.map((item) => {
          if (item.groupKey) {
            const open = !!groupsOpen[item.groupKey];
            const Icon = item.icon;
            const anyChildActive = item.children.some(
              (c) => location.pathname === c.to || location.pathname.startsWith(c.to + '/')
            );
            return (
              <div key={item.groupKey} className={`sidebar-group${open ? ' is-open' : ''}${anyChildActive ? ' has-active' : ''}`}>
                <button
                  type="button"
                  className={`sidebar-link sidebar-group-header${anyChildActive ? ' is-active' : ''}`}
                  onClick={() => toggleGroup(item.groupKey)}
                  title={collapsed ? item.label : undefined}
                  aria-expanded={open}
                >
                  <span className="sidebar-link-icon">
                    <Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} color={item.color} />
                  </span>
                  <span className="sidebar-link-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                    <span>{item.label}</span>
                    <ChevronDown
                      size={14}
                      strokeWidth={2}
                      style={{
                        transition: 'transform 150ms ease',
                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                        color: 'var(--color-text-dim)',
                        marginRight: 4,
                      }}
                    />
                  </span>
                </button>
                {open && (
                  <div className="sidebar-group-children" style={{ paddingLeft: collapsed ? 0 : 12 }}>
                    {item.children.map(({ to, label, icon: ChildIcon, color, end }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={end}
                        className={({ isActive }) => `sidebar-link sidebar-child-link${isActive ? ' is-active' : ''}`}
                        title={collapsed ? label : undefined}
                      >
                        <span className="sidebar-link-icon">
                          <ChildIcon size={ICON_SIZE} strokeWidth={ICON_STROKE} color={color} />
                        </span>
                        <span className="sidebar-link-label">{label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          const { to, label, icon: Icon, color, end } = item;
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `sidebar-link${isActive ? ' is-active' : ''}`}
              title={collapsed ? label : undefined}
            >
              <span className="sidebar-link-icon">
                <Icon size={ICON_SIZE} strokeWidth={ICON_STROKE} color={color} />
              </span>
              <span className="sidebar-link-label">{label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <NavLink
          to="/settings"
          className={({ isActive }) => `sidebar-link${isActive ? ' is-active' : ''}`}
          title={collapsed ? 'Settings' : undefined}
        >
          <span className="sidebar-link-icon">
            <Settings size={ICON_SIZE} strokeWidth={ICON_STROKE} color="#6b7280" />
          </span>
          <span className="sidebar-link-label">Settings</span>
        </NavLink>

        <div className="sidebar-user" title={collapsed ? (user?.name || user?.email || '') : undefined}>
          <div className="sidebar-user-avatar">{initialsFor(user)}</div>
          <div className="sidebar-user-text">
            <span className="sidebar-user-name">{user?.name || 'Unknown user'}</span>
            {user?.email && <span className="sidebar-user-email">{user.email}</span>}
          </div>
        </div>

        <button
          type="button"
          className="sidebar-link sidebar-logout"
          onClick={logout}
          title={collapsed ? 'Sign out' : undefined}
        >
          <span className="sidebar-link-icon">
            <LogOut size={ICON_SIZE} strokeWidth={ICON_STROKE} color="#dc2626" />
          </span>
          <span className="sidebar-link-label">Sign out</span>
        </button>
      </div>
    </aside>
  );
}
// Calendar-style date badge that sits beside the XSIO logo in the
// sidebar header. Reads the current date on every render. The format
// matches the cool mock Dave referenced: red month abbreviation on
// top, large day number below. Updates on the next render after
// midnight (when the component re-renders for any reason); a
// midnight-rollover useEffect could be added later if precise
// minute-of-midnight accuracy ever matters.
function DateBadge() {
  const now = new Date();
  const month = now.toLocaleString('en-CA', { month: 'short' }).toUpperCase();
  const day = String(now.getDate());
  return (
    <div className="date-badge" aria-label={now.toLocaleDateString('en-CA')} role="img">
      <span className="date-badge-month">{month}</span>
      <span className="date-badge-day">{day}</span>
    </div>
  );
}

